import { useEffect, useRef, useState } from "react";
import { FileText, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Attachment } from "@kandoo/shared";
import { api, attachmentUrl } from "../api/client";
import { AttachmentViewer } from "./AttachmentViewer";
import { CollapsibleSection } from "./CollapsibleSection";
import { ConfirmButton } from "./ConfirmButton";
import type { AttachmentUploader } from "../hooks/useAttachmentUpload";

interface CardAttachmentsProps {
  boardId: string;
  cardId: string;
  attachments: Attachment[];
  canEdit: boolean;
  uploader: AttachmentUploader;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const isImage = (mime: string) => mime.startsWith("image/");

export function CardAttachments({
  boardId,
  cardId,
  attachments,
  canEdit,
  uploader,
}: CardAttachmentsProps) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { uploadFiles, isUploading, error } = uploader;

  // Start expanded when the card already has attachments. The initializer only
  // runs on the first render, so a manual collapse survives the card modal's
  // re-renders/refetches.
  const [open, setOpen] = useState(() => attachments.length > 0);
  // Auto-expand the section when an upload begins (e.g. a dropped file) so the
  // result is visible.
  useEffect(() => {
    if (isUploading) setOpen(true);
  }, [isUploading]);

  const [viewerId, setViewerId] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => api.attachments.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["card", cardId] });
      qc.invalidateQueries({ queryKey: ["board", boardId] });
    },
  });

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = "";
  }

  const addButton = canEdit ? (
    <button
      type="button"
      onClick={() => fileRef.current?.click()}
      className="rounded bg-slate-100 px-2 py-1 text-sm text-slate-700 hover:bg-slate-200"
    >
      + Add
    </button>
  ) : undefined;

  return (
    <CollapsibleSection
      title={`Attachments${attachments.length ? ` (${attachments.length})` : ""}`}
      open={open}
      onOpenChange={setOpen}
      action={addButton}
    >
      <input
        ref={fileRef}
        type="file"
        onChange={onFile}
        className="hidden"
      />
      {isUploading && (
        <p className="mb-2 text-sm text-slate-500">Uploading…</p>
      )}
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}

      {attachments.length === 0 ? (
        <p className="text-sm text-slate-500">No attachments.</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-[var(--surface)] p-2"
            >
              <button
                type="button"
                onClick={() => setViewerId(a.id)}
                title={`Preview ${a.filename}`}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded text-left hover:bg-slate-50"
              >
                {isImage(a.mime) ? (
                  <img
                    src={attachmentUrl(a.id)}
                    alt={a.filename}
                    className="h-12 w-12 shrink-0 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-500">
                    <FileText size={20} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800 hover:underline">
                    {a.filename}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {formatBytes(a.size)}
                  </span>
                </div>
              </button>
              {canEdit && (
                <ConfirmButton
                  onConfirm={() => remove.mutate(a.id)}
                  title="Delete attachment"
                  className="rounded px-1.5 py-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-red-600"
                  confirmClassName="rounded px-1.5 text-xs bg-red-100 text-red-700"
                  confirmLabel="Delete?"
                >
                  <X size={14} />
                </ConfirmButton>
              )}
            </li>
          ))}
        </ul>
      )}

      {viewerId && (
        <AttachmentViewer
          attachments={attachments}
          attachmentId={viewerId}
          onClose={() => setViewerId(null)}
        />
      )}
    </CollapsibleSection>
  );
}
